import { memo, useEffect, useMemo, useState } from "react"

import { FeeOption, fetchMediaPlanFees } from "api/plans"
import { SupplierProductTuple, SupplierTuple } from "models/supplier"
import { css, cx } from "@emotion/css"
import {
  formatFixedNumberCurrency,
  formatFloatingPointNumber,
} from "utils/number-format"
import { isSemanticReadonly, usePlanCachedData } from "../utils"
import { queryCache, useQuery } from "react-query"

import { CellError } from "./CellError"
import { CellProps } from "components/TreeState"
import { ChangedPlanRecord } from "../types"
import { checkBuyingStatus } from "utils/type-guards"
import { FieldsKey } from "../components/AutoCompute/types"
import { MediaType } from "routes/Base/Admin/GlobalMediaTypes/types"
import { MultiSelect } from "routes/Base/Admin/components/MultiSelect"
import { Paginated } from "api/types"
import { Tooltip } from "@material-ui/core"
import { get } from "lodash"
import { set } from "lodash/fp"
import { tableAggregations } from "../components/AutoCompute/useAggregations"
import { useAtomValue } from "jotai/utils"
import { useAutoCompute } from "../components/AutoCompute/useAutoCompute"
import { useAutocomputeValidation } from "../useAutocomputeValidation"
import { useBillingTypesRules } from "../useBillingTypeRules"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { useUndoStack } from "../useUndoStack"
import { DEFAULT_PRECISION, usePrecision } from "providers/A12n"
import { roundHalfEven } from "../components/AutoCompute/utils"

export const PreciseFeeCell = memo(function _FeeCell(props: CellProps) {
  const precision = usePrecision()
  return <FeeCell {...props} precision={precision} />
})

export const FeeCell = memo(function _FeeCell(props: CellProps) {
  const {
    rowId: id,
    dataKey,
    columnId,
    disabled,
    semantic,
    format,
    primaryKey,
    precision = DEFAULT_PRECISION,
  } = props
  const monetary = format === "MONETARY"
  const { id: planId } = useParams<{ id: string }>()
  const { t } = useI18n()
  const [selected, setSelected] =
    useState<
      Array<FeeOption & { name?: string; value?: string; type?: string }>
    >()
  const autoCompute = useAutoCompute()
  const tableSource = useTableSource()
  const aggregations = useAtomValue(tableAggregations)
  const writeTo = tableSource.state
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()

  const { handleFocus, hasChanged } = useAutocomputeValidation(selected)

  const action = readFrom[id]?.action
  const updatable = !action || action === "UPDATE_ORDER"
  const error = get(readError, `${id}.${columnId}`)
  const row = get(readFrom, `${id}`) as ChangedPlanRecord

  const type = row?.type
  const packaged = row?.packaged
  const isMediaSubMedia =
    type === "media_type_plan" || type === "media_subtype_plan"

  const isBooking = primaryKey === "buyingMediaFields"
  const planData = usePlanCachedData(isBooking)

  const rawValue = String(get(row, `${dataKey}.value`) ?? "")
  const aggregatedValue = get(aggregations, [id, columnId]) ?? ""
  const hasAggregation = !!Object.keys(aggregations).includes(id)

  let value =
    isMediaSubMedia && hasAggregation && !isBooking
      ? String(aggregatedValue ?? rawValue)
      : rawValue

  const forcedNumeric = !isNaN(Number(value))
  value = forcedNumeric ? roundHalfEven(Number(value)).toString() : value

  const fvalue = monetary
    ? formatFloatingPointNumber(precision, 2)(value)
    : value

  const mediaTypeId = readFrom[id]?.ancestorMediaId

  const isEditable = !isSemanticReadonly(
    type,
    primaryKey as FieldsKey,
    semantic,
    packaged,
  )

  const buyingStatusVal = String(get(readFrom, `${id}.buyingStatus`))
  const buyingStatus = checkBuyingStatus(buyingStatusVal) ? buyingStatusVal : ""

  const isDisabled =
    disabled || !isEditable || buyingStatus === "POSTED_FOR_BILLING"

  const mtps = new URLSearchParams([
    ["configScope", "MEDIA_PLAN"],
    ["configOwnerId", planId],
  ])

  const mediaTypes = queryCache.getQueryData<Paginated<MediaType>>([
    "media-types",
    mtps.toString(),
  ])

  const media = mediaTypes?.data?.find((mt) => mt.id === mediaTypeId)

  const mfps = getFeesParams(
    readFrom[id] as ChangedPlanRecord,
    primaryKey as FieldsKey,
    media,
    semantic,
  )

  const cached = queryCache.getQueryData<Paginated<FeeOption>>([
    "media-plan-fees",
    planId,
    mfps.toString(),
  ])
  const { data, updatedAt } = useQuery(
    ["media-plan-fees", planId, mfps.toString()],
    fetchMediaPlanFees,
    {
      initialData: cached,
      enabled: !cached && !!readFrom[id]?.ancestorMediaSemanticCode,
      refetchOnMount: !cached,
    },
  )

  const options = useMemo(
    () =>
      (data?.data?.map((d) => ({
        name: d.label,
        value: d.id,
        ...d,
      })) as FeeOption[]) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updatedAt],
  )

  const onSubmit = () => {
    tableSource.clearError(id)
    globalUndoState.push([writeTo[id] as ChangedPlanRecord])
    const fees = selected?.map((s) => {
      const { name, value, type, ...keep } = s
      return keep
    })
    writeTo[id] = set(`${dataKey}.fees`, fees, writeTo[id])
    updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))

    hasChanged(selected) &&
      autoCompute?.compute(writeTo[id] as ChangedPlanRecord)
  }

  const handleToggle = (updated: Array<FeeOption>) => {
    setSelected(updated)
  }

  const handleClear = () => {
    setSelected([])
  }

  const handleSelectAll = (on: boolean) => {
    setSelected(on ? options : [])
  }

  useEffect(() => {
    if (!selected) {
      const fees = (get(readFrom[id], `${dataKey}.fees`) ||
        []) as Array<FeeOption>

      if (!!fees?.length) {
        const feeIds = fees.map((f) => f.id)
        const preSelected = options.filter((o) => feeIds.includes(o.id))
        !!preSelected.length && setSelected(preSelected)
      }
    }
  }, [selected, dataKey, id, readFrom, options])

  const canPick = row?.type === "line_item" || row?.type === "package"

  // apply billing type rules
  const viewable = useBillingTypesRules(row, primaryKey, semantic)
  if (!viewable) return <span />

  return canPick ? (
    <div className={styles.tworow}>
      <div>
        <MultiSelect
          checkValues={() => true}
          items={options}
          selectedValues={selected || []}
          getOptionLabel={(option) => option?.name}
          renderOption={(option) => (
            <div className={styles.option}>
              {option?.amount ? (
                <span>
                  {option?.amountType !== "PERCENTAGE"
                    ? formatFixedNumberCurrency(
                        option.amount,
                        planData?.currencyCode,
                        precision,
                      )
                    : option.amount}
                  {option?.amountType === "PERCENTAGE" ? "%" : ""}
                </span>
              ) : null}
              {option?.semantic ? (
                <span>({t(`fee.semantic.${option.semantic}`)})</span>
              ) : null}
              <span>{option.name}</span>
            </div>
          )}
          selectAllOption={
            options.length
              ? { name: t("filters.all"), value: "select-all" }
              : undefined
          }
          selectAllValue="select-all"
          onToggleOption={handleToggle}
          onClearOptions={handleClear}
          onSelectAll={handleSelectAll}
          onFocus={handleFocus}
          onBlur={onSubmit}
          disabled={isDisabled}
          inputValue={fvalue || ""}
          rootClassName={styles.root}
          optionClassName={styles.optionWrapper}
          listClassName={styles.listBox}
          renderInput={(params) => {
            const labels = selected?.map((s) => s.label).join(", ")
            return (
              <div
                ref={params.InputProps.ref}
                id={params.id}
                className={styles.info}
              >
                {!!selected ? (
                  <Tooltip title={labels || ""}>
                    <div
                      className={cx("BodyBody-xsDark-greyRegular", "labels")}
                    >
                      {labels}
                    </div>
                  </Tooltip>
                ) : null}
                <div className="value">
                  {!!fvalue
                    ? formatFixedNumberCurrency(
                        fvalue,
                        planData?.currencyCode,
                        precision,
                      )
                    : ""}
                </div>
                <input {...params.inputProps} />
              </div>
            )
          }}
        />
        {error ? <CellError error={error} /> : null}
      </div>
    </div>
  ) : (
    <span className={styles.readonly}>
      {!!fvalue && !packaged
        ? formatFixedNumberCurrency(fvalue, planData?.currencyCode, precision)
        : null}
    </span>
  )
})

export function getFeesParams(
  li: ChangedPlanRecord,
  fieldsKey: FieldsKey = "planningMediaFields",
  media?: MediaType,
  semantic?: string,
) {
  const { vendor: vendorFieldId, vendorProduct: vendorProductFieldId } =
    media?.defaultFieldIdBySemantic || {}

  const mediaTypeSemanticCode = li?.ancestorMediaSemanticCode

  const vendor = get(li, `${fieldsKey}.${vendorFieldId}`) as SupplierTuple

  const vendorProduct = get(
    li,
    `${fieldsKey}.${vendorProductFieldId}`,
  ) as SupplierProductTuple

  const mfps = new URLSearchParams()
  !!semantic && mfps.set("type", semantic)
  !!vendor && mfps.set("supplierId", vendor?.mediaOwnerId)

  !!vendorProduct &&
    mfps.set("supplierProductId", vendorProduct.supplierProductId)

  !!mediaTypeSemanticCode &&
    mfps.set("mediaTypeSemanticCode", mediaTypeSemanticCode)

  return mfps
}

const styles = {
  root: css`
    .MuiOutlinedInput-notchedOutline,
    .MuiOutlinedInput-notchedOutline:hover,
    .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
      border-width: 0;
    }
  `,
  tworow: css`
    display: flex;
    flex-direction: column;
    width: 100%;
  `,
  option: css`
    font-size: 13px;
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: 35px 48px auto;
    grid-column-gap: var(--spacing-xs);
    > span {
      overflow: hidden;
      max-width: 100%;
      white-space: nowrap;
      text-overflow: ellipsis;
      justify-self: flex-end;
    }
  `,
  info: css`
    width: 100%;
    font-size: 13px;
    font-weight: inherit;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    padding: 2px 0;
    .title {
      min-width: 64px;
    }
    .value {
      height: 100%;
    }
    .labels {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      padding-right: var(--spacing-xxs);
    }
    input {
      width: 100%;
      height: 100%;
      position: absolute;
      border-color: transparent;
      background-color: transparent;
      color: transparent;
      &:hover,
      &:focus,
      &::selection,
      &:active {
        border-color: transparent;
        background-color: transparent;
        color: transparent;
        outline: none;
      }
    }
  `,
  optionWrapper: css`
    padding-top: 0;
    padding-bottom: 0;
  `,
  listBox: css`
    > li {
      > div {
        display: grid;
        grid-template-columns: 1fr 3fr 1fr;
      }
    }
    > li:first-child {
      > div {
        height: inherit;
      }
    }
  `,
  readonly: css`
    width: 100%;
    text-align: end;
  `,
}
